/**
 * File Preview Component
 * Handles preview of different file types: images, PDFs, text files, and Office documents
 */
import React, { useCallback, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { BiX } from 'react-icons/bi';
import { 
  AiOutlineFileText, AiOutlineFilePdf, AiOutlineFileWord, 
  AiOutlineFileExcel, AiOutlineFilePpt, AiOutlineFileZip, 
  AiOutlineFileImage, AiOutlineFileUnknown 
} from "react-icons/ai";
import { BsFileEarmarkText } from "react-icons/bs";
import './FilePreview.scss';

const FilePreview = ({ 
  show, 
  onHide, 
  fileData, 
  fileContent, 
  fileType, 
  onDownload 
}) => {
  const [isLoading, setIsLoading] = useState(false);

  // File type detection and icon mapping
  const getFileTypeInfo = (fileName) => {
    if (!fileName) return { type: 'Unknown File', icon: <AiOutlineFileUnknown size={24} color="#95a5a6" /> };
    
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    
    const fileTypes = {
      // Images
      'jpg': { type: 'JPEG Image', icon: <AiOutlineFileImage size={24} color="#e74c3c" /> },
      'jpeg': { type: 'JPEG Image', icon: <AiOutlineFileImage size={24} color="#e74c3c" /> },
      'png': { type: 'PNG Image', icon: <AiOutlineFileImage size={24} color="#3498db" /> },
      'gif': { type: 'GIF Image', icon: <AiOutlineFileImage size={24} color="#9b59b6" /> },
      'bmp': { type: 'BMP Image', icon: <AiOutlineFileImage size={24} color="#34495e" /> },
      'tiff': { type: 'TIFF Image', icon: <AiOutlineFileImage size={24} color="#2c3e50" /> },
      'webp': { type: 'WebP Image', icon: <AiOutlineFileImage size={24} color="#27ae60" /> },
      
      // Documents
      'pdf': { type: 'PDF Document', icon: <AiOutlineFilePdf size={24} color="#e74c3c" /> },
      'doc': { type: 'Word Document', icon: <AiOutlineFileWord size={24} color="#3498db" /> },
      'docx': { type: 'Word Document', icon: <AiOutlineFileWord size={24} color="#3498db" /> },
      'txt': { type: 'Text File', icon: <AiOutlineFileText size={24} color="#95a5a6" /> },
      'rtf': { type: 'Rich Text File', icon: <AiOutlineFileText size={24} color="#7f8c8d" /> },
      
      // Spreadsheets
      'xls': { type: 'Excel Spreadsheet', icon: <AiOutlineFileExcel size={24} color="#27ae60" /> },
      'xlsx': { type: 'Excel Spreadsheet', icon: <AiOutlineFileExcel size={24} color="#27ae60" /> },
      'csv': { type: 'CSV File', icon: <BsFileEarmarkText size={24} color="#f39c12" /> },
      
      // Presentations
      'ppt': { type: 'PowerPoint Presentation', icon: <AiOutlineFilePpt size={24} color="#e67e22" /> },
      'pptx': { type: 'PowerPoint Presentation', icon: <AiOutlineFilePpt size={24} color="#e67e22" /> },
      
      // Archives
      'zip': { type: 'ZIP Archive', icon: <AiOutlineFileZip size={24} color="#8e44ad" /> },
      'rar': { type: 'RAR Archive', icon: <AiOutlineFileZip size={24} color="#8e44ad" /> },
      '7z': { type: '7-Zip Archive', icon: <AiOutlineFileZip size={24} color="#8e44ad" /> },
      
      // Other
      'xml': { type: 'XML File', icon: <BsFileEarmarkText size={24} color="#e67e22" /> },
      'json': { type: 'JSON File', icon: <BsFileEarmarkText size={24} color="#f39c12" /> },
      'html': { type: 'HTML File', icon: <BsFileEarmarkText size={24} color="#e74c3c" /> },
      'htm': { type: 'HTML File', icon: <BsFileEarmarkText size={24} color="#e74c3c" /> }
    };
    
    return fileTypes[fileExtension] || { 
      type: `${fileExtension?.toUpperCase() || 'Unknown'} File`, 
      icon: <AiOutlineFileUnknown size={24} color="#95a5a6" /> 
    };
  };

  const fileTypeInfo = getFileTypeInfo(fileData?.fileName);

  // Calculate modal height based on screen size
  const calculateModalHeight = useCallback(() => {
    const screenHeight = window.innerHeight;
    const headerHeight = 50; // Reduced header height
    const padding = 10; // Reduced padding for more content space
    
    // Use 95% of available screen height for maximum content display
    const availableHeight = Math.min(Math.max(screenHeight * 0.95, 600), 1200);
    return availableHeight - headerHeight - padding;
  }, []);

  const modalBodyHeight = calculateModalHeight();

  // Handle Office document preview
  const handleOfficePreview = useCallback(() => {
    if (!fileData) return;
    
    // Create a blob URL for the file
    const blob = new Blob([Uint8Array.from(atob(fileData.content), c => c.charCodeAt(0))], {
      type: fileData.contentType
    });
    const blobUrl = URL.createObjectURL(blob);
    
    // Use Microsoft Office Web Viewer
    const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(blobUrl)}`;
    window.open(officeViewerUrl, '_blank');
  }, [fileData]);

  // Render preview content based on file type
  const renderPreviewContent = () => {
    // if (!fileContent && !fileData) {
    //   return (
    //     <div className="file-preview__no-content">
    //       <div className="file-preview__no-content-icon mb-3">
    //         {fileTypeInfo.icon}
    //       </div>
    //       <h5>Preview Not Available</h5>
    //       <p className="text-muted">
    //         This file type cannot be previewed directly. Please download the file to view its contents.
    //       </p>
    //       <Button 
    //         variant="primary" 
    //         onClick={onDownload}
    //         className="file-preview__download-btn"
    //       >
    //         Download File
    //       </Button>
    //     </div>
    //   );
    // }

    // Image preview
    // if (fileData?.contentType?.startsWith('image/')) {
    //   return (
    //     <div className="file-preview__image-container">
    //       <img 
    //         src={fileContent} 
    //         alt={fileData.fileName}
    //         className="file-preview__image"
    //       />
    //     </div>
    //   );
    // }

    // // PDF preview
    // if (fileData?.contentType === 'application/pdf') {
    //   // Add parameters to disable PDF toolbar options
    //   const pdfUrl = `${fileContent}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0&view=FitH`;
      
    //   return (
    //     <div className="file-preview__pdf-container">
    //       <iframe
    //         src={pdfUrl}
    //         className="file-preview__pdf-iframe"
    //         title={fileData.fileName}
    //       />
    //     </div>
    //   );
    // }

    // // Office documents
    // if (fileData?.contentType?.includes('application/vnd.openxmlformats-officedocument') ||
    //     fileData?.contentType?.includes('application/msword') ||
    //     fileData?.contentType?.includes('application/vnd.ms-excel') ||
    //     fileData?.contentType?.includes('application/vnd.ms-powerpoint') ||
    //     ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(fileType)) {
      
    //   return (
    //     <div className="file-preview__office-container">
    //       <div className="file-preview__office-info">
    //         <div className="file-preview__office-icon mb-3">
    //           {fileTypeInfo.icon}
    //         </div>
    //         <h5>{fileData.fileName}</h5>
    //         <p className="text-muted mb-4">
    //           {fileTypeInfo.type} • {Math.round(fileData.content.length / 1024)} KB
    //         </p>
    //         <div className="file-preview__office-actions">
    //           <Button 
    //             variant="outline-primary"
    //             onClick={onDownload}
    //             className="file-preview__action-btn me-3"
    //           >
    //             Download File
    //           </Button>
    //           <Button 
    //             variant="primary"
    //             onClick={handleOfficePreview}
    //             className="file-preview__action-btn"
    //           >
    //             Open in Office Online
    //           </Button>
    //         </div>
    //       </div>
    //     </div>
    //   );
    // }

    // Text files
    // if (fileData?.contentType?.includes('text/') || 
    //     fileData?.contentType === 'application/json' || 
    //     fileData?.contentType === 'application/xml' ||
    //     fileType === 'csv') {
    //   return (
    //     <div className="file-preview__text-container">
    //       <pre className="file-preview__text-content">
    //         {fileContent}
    //       </pre>
    //     </div>
    //   );
    // }

    // Default fallback
    // return (
    //   <div className="file-preview__no-content">
    //     <div className="file-preview__no-content-icon mb-3">
    //       {fileTypeInfo.icon}
    //     </div>
    //     <h5>Preview Not Available</h5>
    //     <p className="text-muted">
    //       This file type cannot be previewed directly. Please download the file to view its contents.
    //     </p>
    //     <Button 
    //       variant="primary" 
    //       onClick={onDownload}
    //       className="file-preview__download-btn"
    //     >
    //       Download File
    //     </Button>
    //   </div>
    // );
  };

  if (!show || !fileData) return null;

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="xl" 
      centered
      className="file-preview-modal"
      dialogClassName="file-preview-modal-dialog"
    >
      <Modal.Header 
        closeButton 
        className="file-preview__header"
      >
        <Modal.Title className="file-preview__title">
          {fileData.fileName}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body 
        className="file-preview__body"
        style={{ height: `${modalBodyHeight}px` }}
      >
        {renderPreviewContent()}
      </Modal.Body>
      
      <Modal.Footer className="file-preview__footer">
        <Button 
          variant="outline-secondary" 
          onClick={onHide}
        >
          Close
        </Button>
        <Button 
          variant="primary" 
          onClick={onDownload}
        >
          Download
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FilePreview; 